/**
 * Persist an issued user certificate + non-extractable CryptoKey in IndexedDB.
 * Private key bytes are never serialized; clearing site data destroys the key.
 */

const DB_NAME = "nevisign-browser-certs";
const DB_VERSION = 1;
const STORE = "user-certs";

export type StoredBrowserCertificate = {
  userId: string;
  issuanceId: string;
  certificatePem: string;
  chainPem: string | null;
  /** Non-extractable; IndexedDB clones CryptoKey, not PKCS#8 bytes. */
  privateKey: CryptoKey;
  publicKey: CryptoKey;
  subject: string;
  serialNumber: string | null;
  notBefore: string | null;
  notAfter: string | null;
  csrFingerprintSha256: string;
  storedAt: string;
};

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "userId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB در دسترس نیست"));
  });
}

function idbRequest<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("عملیات IndexedDB ناموفق بود"));
  });
}

export async function saveBrowserCertificate(record: StoredBrowserCertificate): Promise<void> {
  if (record.privateKey.extractable) {
    throw new Error("کلید خصوصی نباید قابل استخراج باشد");
  }
  if (/PRIVATE KEY/i.test(record.certificatePem) || /PRIVATE KEY/i.test(record.chainPem ?? "")) {
    throw new Error("کلید خصوصی نباید ذخیره شود");
  }
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, "readwrite");
    await idbRequest(tx.objectStore(STORE).put(record));
  } finally {
    db.close();
  }
}

export async function loadBrowserCertificate(
  userId: string,
): Promise<StoredBrowserCertificate | null> {
  if (!userId) return null;
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, "readonly");
    const row = await idbRequest(
      tx.objectStore(STORE).get(userId) as IDBRequest<StoredBrowserCertificate | undefined>,
    );
    if (!row?.privateKey || !row.certificatePem) return null;
    return row;
  } catch {
    return null;
  } finally {
    db.close();
  }
}

export async function deleteBrowserCertificate(userId: string): Promise<void> {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, "readwrite");
    await idbRequest(tx.objectStore(STORE).delete(userId));
  } finally {
    db.close();
  }
}

function bytesToB64(bytes: ArrayBuffer): string {
  const view = new Uint8Array(bytes);
  let bin = "";
  for (let i = 0; i < view.length; i++) bin += String.fromCharCode(view[i] ?? 0);
  return btoa(bin);
}

export async function signWithBrowserCertificate(
  userId: string,
  data: BufferSource,
): Promise<{ signatureB64: string; certificatePem: string; issuanceId: string }> {
  const rec = await loadBrowserCertificate(userId);
  if (!rec) {
    throw new Error("گواهی این مرورگر یافت نشد. دوباره از احراز هویت و گواهی امضا صادر کنید.");
  }
  if (rec.privateKey.extractable) {
    throw new Error("کلید خصوصی نباید قابل استخراج باشد");
  }
  const signature = await crypto.subtle.sign("RSASSA-PKCS1-v1_5", rec.privateKey, data);
  return {
    signatureB64: bytesToB64(signature),
    certificatePem: rec.certificatePem,
    issuanceId: rec.issuanceId,
  };
}
